<template>
  <!-- usage -->
  <!-- <Calendar :range="false" :timePicker="false" v-model="selectedDate" /> -->
  <!-- then watch for the selectedDate model -->
  <!--  -->
  <section v-if="label" class="mb-[-10px]">
    <label class="font-medium text-base leading-5 text-[#000000]">
      {{ label }}
      <span class="text-xl text-red-600" v-if="label && required">*</span>
    </label>
  </section>

  <section
    @click="!disabled && openDatePicker()"
    class="px-4 text-sm rounded-lg w-fit bg-gray-50"
    :class="[
      extraClass,
      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      label
        ? 'py-3 border border-gray-100 text-gray-500 font-medium'
        : 'py-2 border border-gray-300 text-gray-700 font-semibold',
    ]"
  >
    <!-- Overlay -->
    <section
      v-show="isDatePickerVisible"
      class="fixed inset-0 z-50 w-full h-full bg-black bg-opacity-50"
      @click.prevent="closeDatePicker"
    ></section>
    <!--  -->

    <button
      :disabled="disabled"
      class="w-full flex-between"
      :class="`${disabled && 'cursor-not-allowed'}`"
    >
      <section class="flex items-center pr-2 gap-x-2">
        <img
          src="@/assets/svg/calendarSvg.svg"
          alt="date-picker"
          class="w-5 h-5"
          :class="[label ? 'opacity-40' : 'opacity-100']"
        />
        <span>{{ formattedDate || translateText("calendar.selectDate") }}</span>
      </section>

      <i
        class="pi pi-times mt-[2px]"
        v-if="formattedDate && !disabled"
        @click="resetCalendar"
      ></i>

      <img
        v-if="disabled"
        src="@/assets/svg/lock.svg"
        alt=""
        class="cursor-not-allowed size-4 opacity-40"
      />
    </button>

    <div v-show="isDatePickerVisible">
      <Teleport to="body">
        <VueDatePicker
          :range="range"
          :preset-dates="range ? presetDates : []"
          teleport-center
          ref="datePicker"
          :enable-time-picker="time"
          :max-date="maxDate ? moment(maxDate).toDate() : null"
          :min-date="minDate"
          :markers="markers"
          time-picker-inline
          v-model="internalDate"
          @blur="handleDatePickerBlur"
          @update-month-year="handleMonthYearUpdate"
          :month-picker="monthPicker"
        />
      </Teleport>
    </div>
  </section>
</template>

<script>
//libraries
import moment from "moment";

//utils
import { formatDate, translateText, utcDate } from "@/core/helpers/utility";
import {
  endOfMonth,
  startOfWeek,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfYear,
  subMonths,
  subDays,
  subWeeks,
} from "date-fns";

export default {
  name: "Calendar",
  emits: ["update:modelValue", "update-month-year"],
  props: {
    modelValue: {
      default: null,
      type: [String, Array, Object],
    },

    markers: {
      type: Array,
      default: () => [],
    },

    range: {
      type: Boolean,
      default: false,
    },
    maxDate: {
      type: null,
      default: null,
    },
    minDate: {
      type: String,
      default: null,
    },
    time: {
      type: Boolean,
      default: false,
    },

    extraClass: {
      type: String,
      default: "",
    },

    openCalendar: {
      type: Boolean,
      default: false,
      Required: false,
    },
    monthPicker: {
      type: Boolean,
      default: false,
      Required: false,
    },

    label: {
      type: String,
      required: false,
    },

    required: {
      type: Boolean,
      default: false,
      required: false,
    },

    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      utcDate,
      moment,
      translateText,
      isDatePickerVisible: false,
      internalDate: null,
      timeFormat: this.time || this.showTime ? "lll" : "ll",
      presetDates: [
        { label: "Today", value: [new Date(), new Date()] },
        {
          label: "Today (Slot)",
          value: [new Date(), new Date()],
          slot: "preset-date-range-button",
        },
        {
          label: "Yesterday",
          value: [subDays(new Date(), 1), subDays(new Date(), 1)],
        },
        {
          label: "Last 7 days",
          value: [subDays(new Date(), 6), new Date()],
        },
        {
          label: "Last week",
          value: [
            startOfWeek(subWeeks(new Date(), 1)),
            endOfWeek(subWeeks(new Date(), 1)),
          ],
        },
        {
          label: "This month",
          value: [startOfMonth(new Date()), new Date()],
        },
        {
          label: "Last month",
          value: [
            startOfMonth(subMonths(new Date(), 1)),
            endOfMonth(subMonths(new Date(), 1)),
          ],
        },
        {
          label: "This year",
          value: [startOfYear(new Date()), endOfYear(new Date())],
        },
      ],
    };
  },

  computed: {
    formattedDate() {
      const receivedDate = this.modelValue;
      if (!receivedDate) return null;

      if (receivedDate?.startDate) {
        const { startDate, endDate } = receivedDate;
        const formattedStartDate = moment(startDate).format(this.timeFormat);
        const formattedEndDate = moment(endDate).format(this.timeFormat);

        return `${formattedStartDate} - ${formattedEndDate}`;
      } else {
        return moment(receivedDate).format(this.timeFormat);
      }
    },
  },
  watch: {
    internalDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        const newDate = this.formatRange(newVal);
        // console.log(newDate, "bbbbbbb");
        this.$emit("update:modelValue", newDate);
      }
    },

    // modelValue(newVal) {
    //   if (newVal) {
    //     this.internalDate =
    //     this.formatRange(newVal);
    //   }
    // },

    openCalendar() {
      this.openCalendar && this.openDatePicker();
    },
  },

  methods: {
    formatRange(date) {
      if (!date) return null;

      if (Array.isArray(date)) {
        const newDate = {
          startDate: formatDate(date[0]),
          endDate: formatDate(date[1]),
        };

        return newDate;
      } else {
        if (this.time) {
          return moment(date).format("YYYY-MM-DD hh:mm");
        } else {
          return moment(date).format("YYYY-MM-DD");
        }
      }
    },
    openDatePicker() {
      this.isDatePickerVisible = true;

      this.$nextTick(() => {
        const input = this.$refs.datePicker.$el.querySelector("input");
        if (input) {
          input.click();
        }
      });
    },

    resetCalendar(e) {
      e.stopPropagation();
      this.internalDate = null;
      this.isDatePickerVisible = false;
      this.$emit("update:modelValue", null);
    },

    handleDatePickerBlur() {
      // Handle the blur event to close the date picker
      this.isDatePickerVisible = false;
    },

    handleMonthYearUpdate(payload) {
      this.$emit("update-month-year", payload);
    },
  },
};
</script>

<style></style>
